// Published dataset format, shared by the data pipeline (scripts/) and the app.
// Keep this file free of imports so both TS projects can include it.
//
// Files in public/data/ (format version DATASET_VERSION):
//   meta.json              generation date, counts, chains, detail tile keys, shard keys
//   overview.json          pub counts on a coarse grid: the national picture at low zoom
//   shards/<key>.json      the map index for one SHARD_DEG × SHARD_DEG cell, as columnar
//                          arrays (short keys); the app loads only the shards in view
//   search.json            every pub's id, name and rough position, for name search and
//                          deep links; loaded only when needed
//   details/<tile>.json    per-pub detail records, fetched when a pub is tapped
//
// The pipeline builds one full PubIndexFile in memory and splits it.
//
// The dataset is a derived database of OpenStreetMap (ODbL) with FSA hygiene
// ratings (OGL) and must be credited as such.

export const DATASET_VERSION = 2;

/** Bit flags in PubIndex.f. Values from OSM tags unless noted. */
export const Flag = {
  /** beer_garden=yes */
  Garden: 1 << 0,
  /** outdoor_seating=yes */
  Outdoor: 1 << 1,
  /** dog=yes|leashed|outside */
  Dog: 1 << 2,
  /** real_ale=yes */
  RealAle: 1 << 3,
  /** real_cider=yes */
  Cider: 1 << 4,
  /** food=* other than no */
  Food: 1 << 5,
  /** wheelchair=yes */
  StepFree: 1 << 6,
  /** toilets=yes */
  Toilets: 1 << 7,
  /** television=yes or sport=* (pubs tag the sports they show) */
  Sport: 1 << 8,
  /** live_music=yes */
  LiveMusic: 1 << 9,
  /** Derived: brand tag present, or operator runs at least CHAIN_MIN_PUBS pubs */
  Chain: 1 << 10,
  /**
   * Derived (spatial): a beer-garden-like leisure=garden area inside or next
   * to the pub's footprint, or within ~30 m of a pub mapped as a point.
   */
  GardenNearby: 1 << 11,
  /** Derived (spatial): a leisure=outdoor_seating area or point at the pub, as above. */
  OutdoorNearby: 1 << 12,
  /**
   * Derived: tagged beer_garden=no AND outdoor_seating=no, with no spatial
   * evidence. Shown as "No outside space (per OSM)".
   */
  NoOutside: 1 << 13,
  /** Derived: beer_garden=no only (outdoor seating unknown), no positive evidence. */
  NoGarden: 1 << 14,
  /** Derived: outdoor_seating=no only (beer garden unknown), no positive evidence. */
  NoOutdoor: 1 << 15,
} as const;
export type FlagName = keyof typeof Flag;

/** Nation codes in PubIndex.nat (one character per pub). */
export const NATIONS = ['ENG', 'SCT', 'WLS', 'NIR'] as const;
export type Nation = (typeof NATIONS)[number];

/** OSM element type prefix used in pub ids: n123, w456, r789. */
export type OsmType = 'n' | 'w' | 'r';

/**
 * The full map index (built by the pipeline, split into shards). Arrays are parallel: pub i is (id[i], lat[i], lon[i], ...).
 * Coordinates are integers in 1e-5 degrees (~1 m), delta-encoded against the
 * previous pub to compress well; decode with a running sum.
 */
export interface PubIndexFile {
  v: typeof DATASET_VERSION;
  /** ISO date the dataset was generated. */
  gen: string;
  n: number;
  /** "n123", "w456" ... */
  id: string[];
  /** Delta-encoded lat * 1e5. */
  lat: number[];
  /** Delta-encoded lon * 1e5. */
  lon: number[];
  name: string[];
  /** Flag bitmask. */
  f: number[];
  /** Index into `chains` + 1; 0 = independent / unknown. */
  c: number[];
  /** Chain display names. */
  chains: string[];
  /** One digit per pub: index into NATIONS. */
  nat: string;
  /** Detail tile keys (quadkeys). A pub's details live in the longest key that prefixes its own quadkey. */
  tiles: string[];
  /**
   * Opening hours for pubs whose opening_hours tag parses, sparse: ohi[k] is
   * a pub index and ohm[k] its weekly mask (base64 of 168 bits, Monday 00:00
   * first, plus 24 bits for bank holidays when the tag has a PH rule).
   */
  ohi?: number[];
  ohm?: string[];
}

/** Map index shards are cells of this many degrees (latitude and longitude). */
export const SHARD_DEG = 0.5;
/** Overview cells, in degrees. */
export const OVERVIEW_DEG = 0.2;

/** Shard key of a point: "<row>_<col>" of the SHARD_DEG grid, e.g. "103_-5". */
export function shardKey(lat: number, lon: number): string {
  return `${Math.floor(lat / SHARD_DEG)}_${Math.floor(lon / SHARD_DEG)}`;
}

/** Shard keys covering a box (south, west, north, east). */
export function shardKeysIn(south: number, west: number, north: number, east: number): string[] {
  const keys: string[] = [];
  for (let r = Math.floor(south / SHARD_DEG); r <= Math.floor(north / SHARD_DEG); r++) {
    for (let c = Math.floor(west / SHARD_DEG); c <= Math.floor(east / SHARD_DEG); c++) {
      keys.push(`${r}_${c}`);
    }
  }
  return keys;
}

/** meta.json */
export interface DatasetMeta {
  v: typeof DATASET_VERSION;
  /** ISO date the dataset was generated. */
  gen: string;
  /** Total pubs. */
  n: number;
  /** Hash of the content (without `gen`), so an unchanged refresh keeps its date. */
  hash: string;
  chains: string[];
  /** Detail tile keys (quadkeys). */
  tiles: string[];
  /** Shard keys that exist (cells with at least one pub). */
  shards: string[];
}

/**
 * shards/<key>.json: PubIndexFile's columns for one cell. Coordinates are
 * delta-encoded within the shard; `ohi` indexes into this shard.
 */
export type ShardFile = Pick<
  PubIndexFile,
  'n' | 'id' | 'lat' | 'lon' | 'name' | 'f' | 'c' | 'nat' | 'ohi' | 'ohm'
>;

/** overview.json: pubs per OVERVIEW_DEG cell, at the cell's pub centroid (1e-3°). */
export interface OverviewFile {
  lat: number[];
  lon: number[];
  /** Pubs in the cell. */
  n: number[];
  /** Of which with confirmed outside space. */
  g: number[];
}

/** search.json: every pub, for name search and deep links (positions in 1e-3°, ~100 m). */
export interface SearchFile {
  id: string[];
  name: string[];
  lat: number[];
  lon: number[];
  /** 1 when outside space is confirmed (search results get the sort boost). */
  g: number[];
}

/** Bounds of the detail tile quadtree (a square around the UK). */
export const TILE_BOUNDS = { west: -9, south: 49, east: 3, north: 61 } as const;
/** Depth used when computing a pub's quadkey; tiles are prefixes of it. */
export const TILE_MAX_DEPTH = 14;

/** Quadkey of a point within TILE_BOUNDS ("0".."3" per level, NW=0 NE=1 SW=2 SE=3). */
export function quadkey(lat: number, lon: number, depth = TILE_MAX_DEPTH): string {
  let west: number = TILE_BOUNDS.west;
  let south: number = TILE_BOUNDS.south;
  let east: number = TILE_BOUNDS.east;
  let north: number = TILE_BOUNDS.north;
  let key = '';
  for (let d = 0; d < depth; d++) {
    const midLon = (west + east) / 2;
    const midLat = (south + north) / 2;
    const isEast = lon >= midLon;
    const isSouth = lat < midLat;
    key += String((isSouth ? 2 : 0) + (isEast ? 1 : 0));
    if (isEast) west = midLon;
    else east = midLon;
    if (isSouth) north = midLat;
    else south = midLat;
  }
  return key;
}

/** Find the detail tile for a point, given the tile list from the index. */
export function tileFor(lat: number, lon: number, tiles: ReadonlySet<string>): string | null {
  const key = quadkey(lat, lon);
  for (let len = key.length; len >= 0; len--) {
    const prefix = key.slice(0, len);
    if (tiles.has(prefix)) return prefix;
  }
  return null;
}

/** FSA hygiene rating attached to a pub. Labelled "hygiene", never "food quality". */
export interface HygieneRecord {
  /** FHRSID */
  id: number;
  /** "0".."5", "Pass", "Improvement Required", "Exempt", "AwaitingInspection", ... */
  r: string;
  /** Rating date YYYY-MM-DD, or "" if none. */
  d: string;
  /** Scheme: "R" = FHRS (England, Wales, NI), "S" = FHIS (Scotland). */
  s: 'R' | 'S';
}

/** One pub's details in details/<tile>.json (keyed by pub id). */
export interface PubDetails {
  /** Address line without postcode. */
  a?: string;
  /** Town/city. */
  t?: string;
  /** Postcode. */
  pc?: string;
  /** "f" if the address came from the FSA record because OSM had none. */
  as?: 'f';
  /** opening_hours (raw OSM syntax). */
  oh?: string;
  /** Website (raw; sanitise before linking). */
  w?: string;
  /** Phone (raw; sanitise before linking). */
  p?: string;
  /** operator tag. */
  op?: string;
  /** brand tag. */
  br?: string;
  /** Raw values of tags whose nuance the flags lose (dog=leashed, wheelchair=limited, sport=…). */
  tg?: Record<string, string>;
  /** Hygiene rating, if matched. */
  h?: HygieneRecord;
  /** OSM element ("w123", "n456") that is the spatial evidence for GardenNearby/OutdoorNearby. */
  oe?: string;
}

export type DetailTileFile = Record<string, PubDetails>;
